import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { ethers } from 'ethers';
import toast from 'react-hot-toast';
import { api } from '../services/api';

interface WalletContextType {
  address: string | null;
  isConnected: boolean;
  isConnecting: boolean;
  balance: string;
  ndhBalance: string;
  tier: string;
  connectWallet: () => Promise<void>;
  disconnectWallet: () => void;
  signMessage: (message: string) => Promise<string>;
}

const WalletContext = createContext<WalletContextType | undefined>(undefined);

export function WalletProvider({ children }: { children: ReactNode }) {
  const [address, setAddress] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [balance, setBalance] = useState('0');
  const [ndhBalance, setNdhBalance] = useState('0');
  const [tier, setTier] = useState('NONE');

  const fetchBalances = async (addr: string) => {
    try {
      if (window.ethereum) {
        const provider = new ethers.BrowserProvider(window.ethereum);
        const ethBalance = await provider.getBalance(addr);
        setBalance(ethers.formatEther(ethBalance));

        // Simulate NDH balance (in production, fetch from contract)
        const ndh = 0;
        setNdhBalance(String(ndh));
        // Tier se calcula según NDH en staking
        if (ndh >= 100000) setTier('PLATINUM');
        else if (ndh >= 50000) setTier('GOLD');
        else if (ndh >= 10000) setTier('SILVER');
        else if (ndh >= 1000) setTier('BRONZE');
        else setTier('NONE');
      }
    } catch (error) {
      console.error('Error fetching balances:', error);
    }
  };

  const authenticateWithBackend = async (walletAddress: string): Promise<boolean> => {
    try {
      // 1. Build a message for the user to sign
      const message = `Bienvenido a Templum DAO!\n\nFirma este mensaje para autenticarte.\n\nDirección: ${walletAddress}\nTimestamp: ${Date.now()}`;

      // 2. Sign the message with MetaMask
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const signature = await signer.signMessage(message);

      // 3. Send signed message to backend to get JWT
      const authRes = await api.authenticate(walletAddress, message, signature);
      const token = authRes.data?.token;
      if (!token) throw new Error('No token received');

      // 4. Save token
      api.setToken(token);
      return true;
    } catch (error: any) {
      console.error('Backend auth error:', error);
      // If user rejected the signature, propagate that
      if (error.code === 4001 || error.code === 'ACTION_REJECTED') {
        throw new Error('signature_rejected');
      }
      // Other backend errors: warn but don't block wallet connection
      console.warn('Backend authentication failed, some features may be limited:', error.message);
      return false;
    }
  };

  // Use ref to avoid stale closure in useEffect
  const authenticateRef = useRef(authenticateWithBackend);
  authenticateRef.current = authenticateWithBackend;

  useEffect(() => {
    // Restore saved token into api service
    const savedToken = localStorage.getItem('auth_token');
    if (savedToken) {
      api.setToken(savedToken);
    }

    const savedAddress = localStorage.getItem('walletAddress');
    if (savedAddress) {
      setAddress(savedAddress);
      fetchBalances(savedAddress);
      // If we have an address but no token, re-authenticate silently on page load
      if (!savedToken && window.ethereum) {
        authenticateRef.current(savedAddress).catch(() => {
          // Silent fail on page load re-auth (user may need to sign again)
        });
      }
    }

    const handleAccountsChanged = (accounts: string[]) => {
      if (accounts.length === 0) {
        setAddress(null);
        setBalance('0');
        setNdhBalance('0');
        setTier('NONE');
        localStorage.removeItem('walletAddress');
        api.clearToken();
      } else {
        setAddress(accounts[0]);
        localStorage.setItem('walletAddress', accounts[0]);
        fetchBalances(accounts[0]);
      }
    };

    if (window.ethereum) {
      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', () => window.location.reload());
    }

    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      }
    };
  }, []);

  const connectWallet = async () => {
    if (!window.ethereum) {
      toast.error('Por favor instala MetaMask u otra wallet Web3');
      return;
    }

    setIsConnecting(true);

    try {
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts',
      });

      if (accounts.length > 0) {
        const walletAddress = accounts[0];
        setAddress(walletAddress);
        localStorage.setItem('walletAddress', walletAddress);
        await fetchBalances(walletAddress);

        // Authenticate with backend to get JWT
        try {
          const authenticated = await authenticateWithBackend(walletAddress);
          if (authenticated) {
            toast.success('¡Wallet conectada y autenticada!');
          } else {
            toast.success('Wallet conectada (modo limitado)');
          }
        } catch (authErr: any) {
          if (authErr.message === 'signature_rejected') {
            toast.error('Firma rechazada. Algunas funciones no estarán disponibles.');
          } else {
            toast.success('Wallet conectada');
          }
        }
      }
    } catch (error: any) {
      console.error('Error connecting wallet:', error);
      if (error.code === 4001) {
        toast.error('Conexión rechazada por el usuario');
      } else {
        toast.error('Error al conectar la wallet');
      }
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnectWallet = () => {
    setAddress(null);
    setBalance('0');
    setNdhBalance('0');
    setTier('NONE');
    localStorage.removeItem('walletAddress');
    api.clearToken();
    toast.success('Wallet desconectada');
  };

  const signMessage = async (message: string): Promise<string> => {
    if (!window.ethereum || !address) {
      throw new Error('Wallet not connected');
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();
    return signer.signMessage(message);
  };

  return (
    <WalletContext.Provider
      value={{
        address,
        isConnected: !!address,
        isConnecting,
        balance,
        ndhBalance,
        tier,
        connectWallet,
        disconnectWallet,
        signMessage,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
}

export function useWallet() {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
}

declare global {
  interface Window {
    ethereum?: any;
  }
}
